import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';

import { ScrollToCurrentTimeButton } from './scroll-to-current-time-button';
import type { AudioFileViewerSize } from '../audio-file-viewer';
import {
  estimateRowHeight,
  getFallbackColumnWidths,
  measureRowHeight,
  prepareRows,
  readColumnWidths,
  useTranscriptFontReady,
  type TranscriptColumnWidths,
} from './transcript-heights';
import { TranscriptRowItem, type TranscriptRowPhase } from './transcript-row-item';
import {
  buildTranscriptRows,
  findActiveRowIndex,
  findNearestRowIndex,
  findPlayedRowCount,
  findPlayedWordCount,
  mapHighlightsToRows,
  type TranscriptRow,
} from './transcript-row-utils';
import type { AudioBlocks } from '@/api/types/backend-dto-types';
import { SMOOTH_SCROLL_MAX_VIEWPORTS } from '@/new/components/pdf-viewer/constants';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { cn } from '@/new/lib/utils';
import { TimeRangeWithId } from '@/types';

const PADDING_START_PX = 28;
const PADDING_END_PX = 44;
const OVERSCAN_ROWS = 16;
const RESIZE_SETTLE_MS = 10;
const FOCUS_SCROLL_SETTLE_MS = 100;

// Keep this in sync with `pl-10 pr-12` in SCROLLER_CLASSES. The virtual content is
// given an explicit width so its text does not reflow on every resize frame.
const SCROLLER_HORIZONTAL_PADDING_PX = 40 + 48;

// Where a scrolled-to row lands, as a fraction of the viewport height from its top.
const SCROLL_ANCHOR_RATIO = 1 / 3;

// For the dev-only height check
const HEIGHT_TOLERANCE_PX = 2;

const SCROLLER_CLASSES = 'min-h-0 flex-1 overflow-x-hidden overflow-y-auto pl-10 pr-12';

/**
 * Both inputs change only when the playhead crosses a block boundary, so every non-active row
 * gets a phase that is stable across the intervening ticks and its `memo` bails out.
 */
const rowPhase = (index: number, activeRowIndex: number, playedRowCount: number): TranscriptRowPhase => {
  if (index === activeRowIndex) return 'active';
  return index < playedRowCount ? 'played' : 'unplayed';
};

interface AudioTranscriptProps {
  blocks: AudioBlocks | undefined;
  isLoading: boolean;
  currentTime: number;
  isPlaying: boolean;
  seekedTo: { seconds: number } | null;
  onSeek: (seconds: number) => void;
  timeHighlights: TimeRangeWithId[];
  highlightedFactId?: string | null;
  highlightedBlockId?: string | null;
  onHighlightClick?: (id: string) => void;
  onAssignBlockToNewSpeaker?: (blockId: string) => void;
  assigningBlockId?: string | null;
  isAssigningBlock?: boolean;
  size?: AudioFileViewerSize;
}

export function AudioTranscript({
  blocks,
  isLoading,
  currentTime,
  isPlaying,
  seekedTo,
  onSeek,
  timeHighlights,
  highlightedFactId,
  highlightedBlockId,
  onHighlightClick,
  onAssignBlockToNewSpeaker,
  assigningBlockId,
  isAssigningBlock = false,
  size = 'default',
}: AudioTranscriptProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  const rows = useMemo(() => buildTranscriptRows(blocks ?? []), [blocks]);
  const highlightIdsByRow = useMemo(() => mapHighlightsToRows(rows, timeHighlights), [rows, timeHighlights]);

  const isFontReady = useTranscriptFontReady(size);
  const prepared = useMemo(() => (isFontReady ? prepareRows(rows, size) : null), [rows, isFontReady, size]);

  const [scrollerWidth, setScrollerWidth] = useState(0);
  const [measuredWidths, setMeasuredWidths] = useState<TranscriptColumnWidths | null>(null);

  // Memoized because its identity feeds `estimateSize`, which the re-measure effect below keys on.
  const columnWidths = useMemo(
    () => measuredWidths ?? getFallbackColumnWidths(scrollerWidth),
    [measuredWidths, scrollerWidth],
  );

  const isEmpty = !rows.length;

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    setScrollerWidth(scroller.clientWidth);

    let settleTimer: number | undefined;
    const settleWidth = () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => setScrollerWidth(scroller.clientWidth), RESIZE_SETTLE_MS);
    };

    const observer = new ResizeObserver(settleWidth);
    observer.observe(scroller);
    return () => {
      observer.disconnect();
      window.clearTimeout(settleTimer);
    };
  }, [isLoading, isEmpty]);

  const estimateSize = useCallback(
    (index: number) => {
      const row = rows[index];

      if (!prepared || !measuredWidths) return estimateRowHeight(row, columnWidths, size);

      return measureRowHeight(row, prepared[index], measuredWidths, size);
    },
    [rows, prepared, measuredWidths, columnWidths, size],
  );

  const virtualizer = useVirtualizer({
    count: scrollerWidth ? rows.length : 0,
    getScrollElement: () => scrollerRef.current,
    estimateSize,
    getItemKey: (index) => rows[index].blockId,
    overscan: OVERSCAN_ROWS,
    paddingStart: PADDING_START_PX,
    paddingEnd: PADDING_END_PX,
  });

  useLayoutEffect(() => {
    virtualizer.measure();
  }, [virtualizer, estimateSize]);

  const virtualItems = virtualizer.getVirtualItems();
  const hasMountedRows = virtualItems.length > 0;

  /**
   * Replaces the arithmetic fallback with the widths the browser actually laid out. Deps keep this to
   * first mount and resizes rather than every tick, since it forces a layout flush. Returning the
   * current value when nothing moved is what stops it re-rendering itself in a loop.
   */
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !hasMountedRows) return;

    const widths = readColumnWidths(scroller);
    if (!widths) return;

    setMeasuredWidths((current) =>
      current && current.text === widths.text && current.speaker === widths.speaker ? current : widths,
    );
  }, [hasMountedRows, scrollerWidth]);

  /**
   * Parks a row a third of the way down the viewport, so the lines that follow it — the ones you
   * are about to read — get the remaining two thirds. Instant when the row is far enough away that
   * animating would sweep the whole transcript past, the docs viewer's heuristic. Heights are
   * exact, so a single offset scroll lands correctly and smooth scrolling is safe.
   */
  const scrollToRow = useCallback(
    (index: number) => {
      const scroller = scrollerRef.current;
      if (!scroller) return false;

      const rowStart = virtualizer.measurementsCache[index]?.start;
      if (rowStart === undefined) return false;

      // `scrollToOffset` clamps into range, so the first and last rows still land as close as they can.
      const offset = rowStart - scroller.clientHeight * SCROLL_ANCHOR_RATIO;
      const isLongJump = Math.abs(offset - scroller.scrollTop) > scroller.clientHeight * SMOOTH_SCROLL_MAX_VIEWPORTS;

      virtualizer.scrollToOffset(offset, { align: 'start', behavior: isLongJump ? 'auto' : 'smooth' });
      return true;
    },
    [virtualizer],
  );

  /** What to bring into view: the focused block if one is set, else the focused fact's first row. */
  const focusedRowIndex = useMemo(() => {
    if (highlightedBlockId) {
      const blockRowIndex = rows.findIndex((row) => row.blockId === highlightedBlockId);
      if (blockRowIndex >= 0) return blockRowIndex;
    }
    if (!highlightedFactId) return -1;
    return highlightIdsByRow.findIndex((ids) => ids?.includes(highlightedFactId));
  }, [rows, highlightedBlockId, highlightIdsByRow, highlightedFactId]);

  const scrolledFocusKeyRef = useRef<string | null>(null);
  const focusedRowKey =
    focusedRowIndex < 0
      ? null
      : `${highlightedBlockId ? 'block' : 'fact'}:${rows[focusedRowIndex].blockId}:${highlightedBlockId ?? highlightedFactId}`;

  useEffect(() => {
    if (!focusedRowKey) {
      scrolledFocusKeyRef.current = null;
      return;
    }
    if (!measuredWidths || scrolledFocusKeyRef.current === focusedRowKey) return;

    // The facts page reveals its viewer by animating from zero width. Wait until those
    // ResizeObserver updates stop before committing the one-time focus scroll; otherwise
    // the offset is calculated from narrow, over-wrapped rows and clamps to the bottom once
    // the panel reaches its final width. Kim's fixed-width reference panel does not expose
    // this race, which is why the same audio reference already lands correctly there.
    const settleTimer = window.setTimeout(() => {
      if (scrollToRow(focusedRowIndex)) scrolledFocusKeyRef.current = focusedRowKey;
    }, FOCUS_SCROLL_SETTLE_MS);

    return () => window.clearTimeout(settleTimer);
  }, [focusedRowIndex, focusedRowKey, measuredWidths, scrollToRow, scrollerWidth]);

  const activeRowIndex = useMemo(() => findActiveRowIndex(rows, currentTime), [rows, currentTime]);
  const playedRowCount = useMemo(() => findPlayedRowCount(rows, currentTime), [rows, currentTime]);
  const playedWordCount = activeRowIndex < 0 ? 0 : findPlayedWordCount(rows[activeRowIndex], currentTime);

  /**
   * The row the jump-back button chases. Silence between blocks leaves no active row, so it falls
   * back to the nearest one — otherwise the button blinks out for the length of every pause.
   */
  const followedRowIndex = useMemo(
    () => (activeRowIndex >= 0 ? activeRowIndex : findNearestRowIndex(rows, currentTime)),
    [activeRowIndex, rows, currentTime],
  );

  // Read at call time, so it stays stable while still seeing the latest measurements.
  const getRowBounds = useCallback((index: number) => virtualizer.measurementsCache[index], [virtualizer]);

  // Scroll to row on seek. Nearest rather than active, so scrubbing into the silence at either
  // end of the recording still lands somewhere.
  const scrolledSeekRef = useRef<{ seconds: number } | null>(null);

  useEffect(() => {
    if (!seekedTo || !measuredWidths || scrolledSeekRef.current === seekedTo) return;

    const index = findNearestRowIndex(rows, seekedTo.seconds);
    if (index < 0) return;

    if (scrollToRow(index)) scrolledSeekRef.current = seekedTo;
  }, [seekedTo, rows, measuredWidths, scrollToRow]);

  // Auto-scroll unmounts the focused row, which drops focus to `<body>` and silently breaks
  // the viewer's space-to-toggle handler. Restore it if that row comes back.
  const focusedBlockIdRef = useRef<string | null>(null);

  const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    focusedBlockIdRef.current =
      (event.target as HTMLElement).closest<HTMLElement>('[data-block-id]')?.dataset.blockId ?? null;
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    // A `relatedTarget` means the user moved focus themselves, so there is nothing to restore.
    // Focus lost to an unmounting row leaves it null, which is the case worth remembering.
    if (event.relatedTarget) focusedBlockIdRef.current = null;
  };

  useEffect(() => {
    const blockId = focusedBlockIdRef.current;
    if (!blockId || document.activeElement !== document.body) return;

    scrollerRef.current
      ?.querySelector<HTMLButtonElement>(`[data-block-id="${CSS.escape(blockId)}"]`)
      ?.focus({ preventScroll: true });
  }, [virtualItems]);

  // Dev-only check on the row heights to make sure virtualisation isn't cooked
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    virtualItems.forEach((item) => {
      const element = scrollerRef.current?.querySelector<HTMLElement>(`[data-row-index="${item.index}"]`);
      if (!element) return;

      // offsetHeight is a layout measurement, so the popover's opening scale transform does not
      // produce false row-height warnings.
      const actualHeight = element.offsetHeight;
      if (Math.abs(actualHeight - item.size) <= HEIGHT_TOLERANCE_PX) return;

      // Verbose on purpose, and it recomputes the height rather than only reporting the cached one:
      // a `recomputed` that matches the render means the model is right and the cached size is
      // stale, which is a completely different bug from the model itself being wrong.
      const recomputed =
        prepared && measuredWidths
          ? measureRowHeight(rows[item.index], prepared[item.index], measuredWidths, size)
          : null;

      console.warn(
        `[audio-transcript] computed row height is off by ${(actualHeight - item.size).toFixed(1)}px`,
        `(row ${item.index}, cached ${item.size}, recomputed ${recomputed ?? 'n/a'},`,
        `rendered ${actualHeight.toFixed(1)}, measured widths ${measuredWidths ? `${measuredWidths.text.toFixed(2)}/${measuredWidths.speaker.toFixed(2)}` : 'none'})`,
        rows[item.index].text,
      );
    });
  }, [virtualItems, rows, prepared, measuredWidths, size]);

  const handleActivate = useCallback(
    (row: TranscriptRow, highlightIds: string[] | null, isHighlightedFact: boolean) => {
      onSeek(row.startTimeSeconds);
      const clickedHighlightId = isHighlightedFact ? highlightedFactId : highlightIds?.[0];
      if (clickedHighlightId) onHighlightClick?.(clickedHighlightId);
    },
    [onSeek, onHighlightClick, highlightedFactId],
  );

  if (isLoading) {
    return (
      <div className={cn(SCROLLER_CLASSES, 'pt-7 pb-11')} aria-busy>
        <div className="space-y-10">
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className="grid grid-cols-[6.75rem_3.25rem_minmax(0,1fr)] gap-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="ml-auto h-4 w-10" />
              <div className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className={cn(SCROLLER_CLASSES, 'pt-7 pb-11')}>
        <p className={cn('py-12 text-center text-gray-600', size === 'sm' ? 'text-xs' : 'text-sm')}>
          No transcript is available for this audio file.
        </p>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <ScrollToCurrentTimeButton
        scrollerRef={scrollerRef}
        rowIndex={followedRowIndex}
        getRowBounds={getRowBounds}
        onActivate={() => scrollToRow(followedRowIndex)}
        resizeKey={scrollerWidth}
      />

      <div ref={scrollerRef} className={SCROLLER_CLASSES} role="list" onFocus={handleFocus} onBlur={handleBlur}>
        <div
          role="presentation"
          className="relative"
          style={{
            height: virtualizer.getTotalSize(),
            width: Math.max(1, scrollerWidth - SCROLLER_HORIZONTAL_PADDING_PX),
          }}
        >
          {/* One translated wrapper rather than positioning each row, so a tick builds far fewer style objects. */}
          <div
            role="presentation"
            className="absolute top-0 left-0 w-full"
            style={{ transform: `translateY(${virtualItems[0]?.start ?? 0}px)` }}
          >
            {virtualItems.map((item) => (
              <TranscriptRowItem
                key={item.key}
                row={rows[item.index]}
                index={item.index}
                rowCount={rows.length}
                highlightIds={highlightIdsByRow[item.index]}
                isHighlightedFact={!!highlightedFactId && !!highlightIdsByRow[item.index]?.includes(highlightedFactId)}
                isHighlightedBlock={rows[item.index].blockId === highlightedBlockId}
                isPlaying={isPlaying}
                phase={rowPhase(item.index, activeRowIndex, playedRowCount)}
                playedWordCount={item.index === activeRowIndex ? playedWordCount : 0}
                onActivate={handleActivate}
                onAssignToNewSpeaker={
                  !isAssigningBlock || assigningBlockId === rows[item.index].blockId
                    ? onAssignBlockToNewSpeaker
                    : undefined
                }
                isAssigningToNewSpeaker={assigningBlockId === rows[item.index].blockId}
                isNewSpeakerAssignmentDisabled={isAssigningBlock}
                size={size}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
